// src/stores/useLayerCoverageStore.js
//
// Coverage map for the Features-tab asset types within the active operating
// AOI. Drives FeaturesTab filtering, ActiveLayers indicators, and any other
// "what's present in this AOI" UI. When `coverage` is null (no AOI, loading,
// or fetch failed) consumers should fall back to showing everything.
//
// Modeled on useImageryStore: same loading/error pattern, fetched via the
// shared auth fetcher. See useSyncOperatingAoiCoverage for the AOI-change
// wiring.

import { create } from "zustand";
import { fetchWithAuth } from "../api/fetchWithAuth";

export const useLayerCoverageStore = create((set) => ({
  // ---- State ----
  coverage: null,   // { [assetType]: { present: boolean, count: number | null } } | null
  loading: false,
  error: null,
  aoiId: null,      // the AOI this coverage was loaded for

  // ---- Actions ----
  loadCoverage: async (operatingAoiId) => {
    // The endpoint 400s without an AOI; the global-mode path is clear().
    if (!operatingAoiId) return;

    set({ loading: true, error: null });

    try {
      const res = await fetchWithAuth(
        `/api/v1/layer-coverage?operating_aoi=${encodeURIComponent(operatingAoiId)}`
      );
      if (!res.ok) {
        const body = await res.json().catch((e) => { console.error("[ui/src/stores/useLayerCoverageStore.js] res.json() failed (swallowed):", e); return {}; });
        throw new Error(body.error || `HTTP ${res.status}`);
      }
      const data = await res.json();
      set({
        coverage: data.coverage ?? null,
        aoiId: data.operating_aoi ?? operatingAoiId,
        loading: false,
      });
    } catch (err) {
      // Leave coverage as null on failure so the UI falls back to "show
      // everything" rather than hiding layers it can't verify.
      console.error("[useLayerCoverageStore] loadCoverage", err);
      set({
        coverage: null,
        loading: false,
        error: err.message ?? String(err),
      });
    }
  },

  clear: () =>
    set({ coverage: null, loading: false, error: null, aoiId: null }),
}));
